import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { adminApi, type Pathway } from '@/api';

const ICON_OPTIONS = ['BrainCircuit', 'Zap', 'Cpu', 'Scale', 'Users', 'Palette', 'Briefcase', 'BarChart3', 'Megaphone', 'ShoppingBag', 'ShieldAlert', 'LayoutGrid'];

export function PathwayForm() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Pathway>({
    id: '',
    name: '',
    order: 0,
    icon: null,
  });

  useEffect(() => {
    if (isNew) return;
    adminApi.pathways.get(id!).then(setForm).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.pathways.create(form);
        navigate('/pathways');
      } else {
        await adminApi.pathways.update(id!, {
          name: form.name,
          order: form.order,
          icon: form.icon,
        });
        navigate('/pathways');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Link to="/pathways" className="text-slate-600 hover:text-slate-900 text-sm">
          ← Pathways
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800">{isNew ? 'New pathway' : 'Edit pathway'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ID</label>
          <input
            value={form.id}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            required
            disabled={!isNew}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
            placeholder="e.g. pathway-foundation"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="e.g. Foundation"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            min={0}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Icon (ICON_MAP key, optional)</label>
          <select
            value={form.icon ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value || null }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          >
            <option value="">None</option>
            {ICON_OPTIONS.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
        {error && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to="/pathways" className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
